import { useEffect, useRef, useState } from 'react';
import { fetchConvertedDocument, type ConvertibleThumbKind } from '../../lib/converted-document';
import { RichFilePreview } from './RichFilePreview';

/**
 * A `.docx`/`.pptx` thumbnail: the pod's markdown conversion, rendered as a miniature.
 *
 * Neither format can be read in a browser — both are zips of xml — so unlike every other preview here this
 * one costs a server round trip. Same two guards as the PDF thumbnail, for the same reason:
 *
 *  - **converts only once the card is in view** (an IntersectionObserver), so a long gallery does not fire
 *    a conversion per row;
 *  - **converts once per URL** — the shared React Query cache with an infinite `staleTime`, since a
 *    conversion is deterministic for a given file and the viewer wants the same result.
 *
 * A failed conversion falls back to the type glyph rather than an error state: a thumbnail is a decoration,
 * and the file still opens.
 */
export function ConvertedThumb({
  url,
  kind,
  fallback,
}: {
  url: string;
  kind: ConvertibleThumbKind;
  fallback: React.ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // The pod returns rendered HTML now, not markdown — the variable keeps its name because it is still
  // just 'the converted body this card draws'.
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || text || typeof IntersectionObserver === 'undefined') return undefined;
    let cancelled = false;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      void fetchConvertedDocument(url, kind).then((converted) => {
        if (!cancelled && converted) setText(converted);
      });
    });
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [url, kind, text]);

  return (
    <div ref={hostRef} style={{ width: '100%', height: '100%', borderRadius: 'inherit', overflow: 'hidden' }}>
      {text ? <RichFilePreview kind="html" body={text} lines={[]} /> : fallback}
    </div>
  );
}
